import { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemIcon, Checkbox, Chip, Divider, 
  LinearProgress, Alert, TextField, Grid
} from '@mui/material';
import CollectionsIcon from '@mui/icons-material/Collections';
import RefreshIcon from '@mui/icons-material/Refresh';
import { collectionsAPI } from '../../services/api';

const GenerateCollectionsDialog = ({ open, onClose, onSave }) => {
  const [generatingCollections, setGeneratingCollections] = useState(false);
  const [generatedCollections, setGeneratedCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [error, setError] = useState(null);
  const [collectionCount, setCollectionCount] = useState(5); // Default to 5 collections
  const [userPrompt, setUserPrompt] = useState(''); // New state for user's prompt/wishes

  // When dialog opens, don't automatically generate - let user set count first
  useEffect(() => {
    if (open) {
      setGeneratedCollections([]);
      setSelectedCollections([]);
      setError(null);
    }
  }, [open]);

  const handleCountChange = (e) => {
    const value = parseInt(e.target.value, 10);
    // Make sure count is between 1 and 20
    setCollectionCount(Math.min(Math.max(value || 1, 1), 20));
  };

  const handlePromptChange = (e) => {
    setUserPrompt(e.target.value);
  };

  const generateCollections = async () => {
    try {
      setGeneratingCollections(true);
      setError(null);
      
      // Call the API to generate collections with the specified count and user prompt
      const response = await collectionsAPI.generateCollections(collectionCount, userPrompt);
      
      // Set the generated collections
      setGeneratedCollections(response.data.collections || []);
      setSelectedCollections([]); // Reset selections
      
    } catch (error) {
      console.error('Error generating collections:', error);
      setError('Failed to generate collections. Please try again.');
    } finally {
      setGeneratingCollections(false);
    }
  };

  const handleToggleCollection = (collection) => {
    const currentIndex = selectedCollections.findIndex(c => c.title === collection.title);
    const newSelected = [...selectedCollections];

    if (currentIndex === -1) {
      newSelected.push(collection);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedCollections(newSelected);
  };

  const handleSaveSelectedCollections = async () => {
    if (selectedCollections.length === 0) {
      setError('No collections selected');
      return;
    }

    try {
      setGeneratingCollections(true);
      
      // Create each selected collection
      const savePromises = selectedCollections.map(collection => 
        collectionsAPI.createCollection({
          title: collection.title,
          description: collection.description,
          books: collection.books.map(book => book.id),
          status: 'draft'
        })
      );
      
      const results = await Promise.all(savePromises);
      
      // Call onSave callback with saved collections
      if (onSave) {
        onSave(selectedCollections, results);
      }
      
      // Close dialog
      onClose();
      
    } catch (error) {
      console.error('Error saving collections:', error);
      setError('Failed to save collections');
    } finally {
      setGeneratingCollections(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => !generatingCollections && onClose()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        AI-Generated Collections
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Collection generation settings */}
        <Box sx={{ mb: 3, mt: 1 }}>
          <Grid container spacing={2}>
            {/* Collection count input */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Number of Collections"
                type="number"
                value={collectionCount}
                onChange={handleCountChange}
                InputProps={{ inputProps: { min: 1, max: 20 } }}
                fullWidth
                variant="outlined"
                size="small"
                disabled={generatingCollections}
                helperText="Min: 1, Max: 20"
              />
            </Grid>
            
            {/* User prompt/wishes input */}
            <Grid item xs={12}>
              <TextField
                label="What kind of collections would you like to generate?"
                value={userPrompt}
                onChange={handlePromptChange}
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                disabled={generatingCollections}
                placeholder="E.g., 'Business books for entrepreneurs' or 'Collections around personal development themes'"
                helperText="Describe the themes, topics, or types of collections you'd like to generate"
              />
            </Grid>
            
            {/* Generate button */}
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={generateCollections}
                disabled={generatingCollections}
                startIcon={generatingCollections ? <LinearProgress size={24} /> : <RefreshIcon />}
                sx={{ minWidth: 200 }}
              >
                {generatingCollections ? 'Generating...' : generatedCollections.length > 0 ? 'Regenerate Collections' : 'Generate Collections'}
              </Button>
            </Grid>
          </Grid>
        </Box>
        
        {generatingCollections ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              AI is analyzing your library and creating {collectionCount} thematic collections
              {userPrompt ? ' based on your requirements' : ''}...
            </Typography>
            <LinearProgress />
          </Box>
        ) : generatedCollections.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {error ? 'Please try again with different settings.' : 'Click the "Generate Collections" button to start.'}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the collections you want to save to your library. Each collection includes suggested
              books that fit the theme.
            </Typography>
            
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {generatedCollections.map((collection, index) => (
                <Box key={collection.title || index}>
                  <ListItem
                    button
                    onClick={() => handleToggleCollection(collection)}
                    alignItems="flex-start"
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedCollections.some(c => c.title === collection.title)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold">
                          {collection.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                            {collection.description}
                          </Typography>
                          <Typography component="div" variant="body2" color="text.secondary">
                            Books: {collection.books?.length || 0}
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {collection.books?.slice(0, 5).map((book) => (
                                <Chip 
                                  key={book.id} 
                                  label={book.title} 
                                  size="small" 
                                  variant="outlined"
                                />
                              ))}
                              {collection.books?.length > 5 && (
                                <Chip 
                                  label={`+${collection.books.length - 5} more`} 
                                  size="small" 
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < generatedCollections.length - 1 && <Divider component="li" />}
                </Box>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          disabled={generatingCollections}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSaveSelectedCollections}
          variant="contained"
          color="primary"
          disabled={generatingCollections || selectedCollections.length === 0}
          startIcon={<CollectionsIcon />}
        >
          Save Selected ({selectedCollections.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateCollectionsDialog;
